import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, Check, X, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PilotSection = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '',
    company: '',
    phone: '',
    use_case: '',
  });

  const updateField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const closeModal = useCallback(() => {
    setOpen(false);
    setError('');
    if (submitted) {
      setSubmitted(false);
      setForm({ name: '', email: '', role: '', company: '', phone: '', use_case: '' });
    }
  }, [submitted]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
        await axios.post(`${API}/pilot/signup`, {
          ...form,
          language: i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl',
        });
        setSubmitted(true);
      } catch (err) {
        setError(t('pilot.errorGeneric'));
      } finally {
        setLoading(false);
      }
    },
    [form, i18n.resolvedLanguage, t]
  );

  const perks = t('pilot.perks', { returnObjects: true });

  return (
    <>
      <section className="hussar-pilot-section" id="pilot" data-testid="pilot-section">
        <div className="hussar-container">
          <div className="hussar-pilot-inner">
            <div className="hussar-pilot-badge">
              <Rocket className="w-4 h-4" />
              <span>{t('pilot.badge')}</span>
            </div>
            <h2 className="hussar-pilot-title">{t('pilot.title')}</h2>
            <p className="hussar-pilot-subtitle">{t('pilot.subtitle')}</p>
            <ul className="hussar-pilot-perks">
              {perks.map((perk) => (
                <li key={perk}>
                  <Check className="w-4 h-4" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => setOpen(true)}
              className="hussar-btn-primary hussar-btn-large"
              data-testid="pilot-apply-btn"
            >
              {t('pilot.applyButton')}
            </Button>
            <p className="hussar-pilot-safety-line" data-testid="pilot-safety-line">
              {t('pilot.safetyLine')}
            </p>
          </div>
        </div>
      </section>

      {open && (
        <div
          className="hussar-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          data-testid="pilot-modal"
        >
          <Card
            className="hussar-pilot-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              className="hussar-modal-close"
              aria-label={t('pilot.close')}
              data-testid="pilot-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {!submitted ? (
              <>
                <h3 className="hussar-pilot-modal-title">{t('pilot.formTitle')}</h3>
                <p className="hussar-pilot-modal-subtitle">{t('pilot.formSubtitle')}</p>
                <form onSubmit={handleSubmit} className="hussar-pilot-form">
                  <div className="hussar-form-group">
                    <label className="hussar-form-label">{t('pilot.fieldName')}</label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={updateField('name')}
                      required
                      minLength={2}
                      className="hussar-form-input"
                      data-testid="pilot-name-input"
                    />
                  </div>
                  <div className="hussar-form-group">
                    <label className="hussar-form-label">{t('pilot.fieldEmail')}</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={updateField('email')}
                      required
                      className="hussar-form-input"
                      data-testid="pilot-email-input"
                    />
                  </div>
                  <div className="hussar-form-group">
                    <label className="hussar-form-label">{t('pilot.fieldRole')}</label>
                    <Input
                      type="text"
                      value={form.role}
                      onChange={updateField('role')}
                      required
                      placeholder={t('pilot.fieldRolePlaceholder')}
                      className="hussar-form-input"
                      data-testid="pilot-role-input"
                    />
                  </div>
                  <div className="hussar-pilot-form-row">
                    <div className="hussar-form-group">
                      <label className="hussar-form-label">{t('pilot.fieldCompany')}</label>
                      <Input
                        type="text"
                        value={form.company}
                        onChange={updateField('company')}
                        className="hussar-form-input"
                        data-testid="pilot-company-input"
                      />
                    </div>
                    <div className="hussar-form-group">
                      <label className="hussar-form-label">{t('pilot.fieldPhone')}</label>
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={updateField('phone')}
                        className="hussar-form-input"
                        data-testid="pilot-phone-input"
                      />
                    </div>
                  </div>
                  <div className="hussar-form-group">
                    <label className="hussar-form-label">{t('pilot.fieldUseCase')}</label>
                    <Textarea
                      value={form.use_case}
                      onChange={updateField('use_case')}
                      required
                      minLength={10}
                      placeholder={t('pilot.fieldUseCasePlaceholder')}
                      className="hussar-form-input"
                      rows={4}
                      data-testid="pilot-usecase-input"
                    />
                  </div>
                  {error && <p className="hussar-form-error">{error}</p>}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="hussar-btn-primary hussar-btn-full hussar-btn-large"
                    data-testid="pilot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('pilot.submittingButton')}
                      </>
                    ) : (
                      t('pilot.submitButton')
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="hussar-pilot-success" data-testid="pilot-success">
                <CheckCircle className="w-14 h-14" style={{ color: '#00D4FF' }} />
                <h3 className="hussar-pilot-modal-title">{t('pilot.successTitle')}</h3>
                <p className="hussar-pilot-modal-subtitle">{t('pilot.successMessage')}</p>
                <Button onClick={closeModal} className="hussar-btn-primary">
                  {t('pilot.close')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
};

export default PilotSection;
